/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import _get from 'lodash/get'
import _pick from 'lodash/pick'
import { createSelector } from 'reselect'
import { EMPTY_ARRAY, EMPTY_OBJECT } from 'constants/frozenObjects'
import { API_TYPES } from 'constants/apiTypes'
import { DEBUG_LANGUAGE } from 'constants/localization'

export const getEntities = state => state.entities
export const getEntitiesByType = type => state => _get(getEntities(state), type, EMPTY_OBJECT)
export const getEntitiesNameById = type => (state, id) => _get(getEntities(state), [type, id, 'Name'])
export const getEntitiesById = type => (state, { id }) => _get(getEntities(state), [type, id])

export const getCountryById = getEntitiesById(API_TYPES.countries)
export const getStateById = (state, { id }) => getStates(state).find(state => state.id === id)
export const isUserCountryResidencePermitted = (state, { id }) =>
  _get(getEntitiesById(API_TYPES.userCountriesResidence)(state, { id }), 'isPermitted')

export const getApiDataByType = type => state => _get(state.apiData, `${type}.value`, EMPTY_ARRAY)

export const getLoadStatusByType = (state, type) => _get(state.apiData, `${type}.loading`)
export const isLoadedByType = (state, type) => _get(state.apiData, `${type}.loaded`)
export const isLoadedAllTypes = (state, types = []) => types.every(type => isLoadedByType(state, type))
export const getLoadingStatus = (state, types = []) => types.map(type => getLoadStatusByType(state, type))

export const getLanguages = getEntitiesByType(API_TYPES.languages)
export const getCompanyCountriesResidence = getEntitiesByType(API_TYPES.companyCountriesResidence)
export const getUserCountriesResidence = getEntitiesByType(API_TYPES.userCountriesResidence)
export const getMyCompanyInfo = getApiDataByType(API_TYPES.myCompanyInfo)
export const getStates = getApiDataByType(API_TYPES.states)

export const getLanguageOptions = createSelector(
  getLanguages,
  (languages = {}) => {
    const result = Object.values(languages)
      .map(({ code, name }) => ({
        label: name,
        value: code,
      }))

    if (__IS_DEBUG_LOCALIZATION__) {
      return result.concat({
        label: 'DEBUG',
        value: DEBUG_LANGUAGE,
      })
    }

    return result
  },
)

export const getEntitiesArrayByType = type => createSelector(
  getApiDataByType(type),
  getEntitiesByType(type),
  (result, entities) => Object.values(_pick(entities, result)),
)

const getPropsFromItems = (items) =>
  items.map(props => {
    const label = _get(props, 'name', '[NO_NAME]')
    const value = _get(props, 'id')
    return {
      label,
      value,
    }
  })

const getStatePropsFromItems = (items) =>
  items.map(props => {
    const isoCode = _get(props, 'isoCode')
    const value = _get(props, 'id')
    return {
      isoCode,
      value,
    }
  })

export const getItemsOptionsCreator = type => createSelector(
  getEntitiesArrayByType(type),
items => getPropsFromItems(items),
)

export const getStateItemsOptionsCreator = type => createSelector(
  getApiDataByType(type),
  items => getStatePropsFromItems(items),
)

export const getCountryOptions = getItemsOptionsCreator(API_TYPES.countries)
export const getCompanyCountryResidenceOptions = getItemsOptionsCreator(API_TYPES.companyCountriesResidence)
export const getUserCountryResidenceOptions = getItemsOptionsCreator(API_TYPES.userCountriesResidence)
export const getStateOptions = getStateItemsOptionsCreator(API_TYPES.states)

const getSingleEntityByType = type => state =>
  _get(getEntitiesByType(type)(state), getApiDataByType(type)(state), EMPTY_OBJECT)

export const getMyCompanyDetails = getSingleEntityByType(API_TYPES.myCompanyDetails)

export const getLocalizedUserCountryResidenceOptions = createSelector(
  getUserCountryResidenceOptions,
  (state, { t }) => t,
  (state, { texts }) => texts,
  (countryResidenceOptions, t) => countryResidenceOptions.map(({ value }) => ({
    label: t(`countries.${value}`),
    value,
  })),
)

export const getLocalizedCompanyCountryResidenceOptions = createSelector(
  getCompanyCountryResidenceOptions,
  (state, { t }) => t,
  (state, { texts }) => texts,
  (countryResidenceOptions, t) => countryResidenceOptions.map(({ value }) => ({
    label: t(`countries.${value}`),
    value,
  })),
)

export const getLocalizedBillingCountryOptions = createSelector(
  getCountryOptions,
  (state, { t }) => t,
  (state, { texts }) => texts,
  (countryOptions, t) => countryOptions.map(({ value }) => ({
    label: t(`countries.${value}`),
    value,
  })),
)

export const getLocalizedStateOptions = createSelector(
  getStateOptions,
  (state, { t }) => t,
  (state, { texts }) => texts,
  (stateOptions, t) => stateOptions.map(state => ({
    label: state.isoCode,
    value: state.value,
  })),
)

// except Alaska and Hawaii
export const getLocalizedLimitedStateOptions = createSelector(
  getStateOptions,
  (state, { t }) => t,
  (state, { texts }) => texts,
  (stateOptions, t) => stateOptions.filter(state => state.isoCode != 'AK' && state.isoCode != 'HI').map(state => ({
    label: state.isoCode,
    value: state.value,
  })),
)

export const getRecaptchaWhiteListConfig = getApiDataByType(API_TYPES.recaptchaWhiteList)
export const getAppConfig = getApiDataByType(API_TYPES.appConfig)
export const isCaptchaEnabled = state => _get(getAppConfig(state), 'googleRecaptchaEnabled', false)
export const isCaptchaWhiteListed = state => _get(getRecaptchaWhiteListConfig(state), 'recaptcha', false)
export const googleRecaptchaSiteKey = state => _get(getAppConfig(state), 'googleRecaptchaSiteKey', '')
export const isMaintenanceModeOn = state => _get(getAppConfig(state), 'maintenanceModeIsOn', false)
export const maintenanceMessage = state => _get(getAppConfig(state), 'maintenanceMessage')
